import React, { useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BarChart3, TrendingUp, Users, AlertTriangle, Eye, FileCheck, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  status?: 'healthy' | 'warning' | 'critical' | 'neutral';
}

const statusColors: Record<string, string> = {
  healthy: 'bg-green-500/10 text-green-700 border-green-200 dark:text-green-400 dark:border-green-800',
  warning: 'bg-yellow-500/10 text-yellow-700 border-yellow-200 dark:text-yellow-400 dark:border-yellow-800',
  critical: 'bg-destructive/10 text-destructive border-destructive/20',
  neutral: 'bg-muted text-muted-foreground border-border',
};

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon, status = 'neutral' }) => (
  <Card className={`border ${statusColors[status]}`}>
    <CardContent className="pt-4 pb-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium opacity-70">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs opacity-60 mt-0.5">{subtitle}</p>}
        </div>
        <div className="opacity-50">{icon}</div>
      </div>
    </CardContent>
  </Card>
);

function getConversionStatus(rate: number): 'healthy' | 'warning' | 'critical' {
  if (rate >= 60) return 'healthy';
  if (rate >= 30) return 'warning';
  return 'critical';
}

function getEvidenceStatus(pct: number): 'healthy' | 'warning' | 'critical' {
  if (pct < 30) return 'healthy';
  if (pct <= 70) return 'warning';
  return 'critical';
}

function getUsageStatus(count: number): 'healthy' | 'warning' | 'critical' {
  if (count >= 4) return 'healthy';
  if (count >= 1) return 'warning';
  return 'critical';
}

const UxAdoptionMetricsPanel: React.FC = () => {
  const { toast } = useToast();

  const exportCSV = useCallback(async () => {
    try {
      const fourteenDaysAgo = new Date(Date.now() - 14 * 86400000).toISOString();
      const { data: events, error } = await supabase
        .from('emp_eventos_negocio')
        .select('tipo_evento, created_at, user_id, payload')
        .in('tipo_evento', [
          'quick_capture_aberto', 'quick_capture_submit',
          'execucao_registrada_quick_capture',
          'cockpit_aba_proximo_acessada', 'cockpit_aba_saldos_acessada',
        ])
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!events?.length) {
        toast({ title: 'Sem dados para exportar', variant: 'destructive' });
        return;
      }

      const rows = events.map(e => ({
        tipo_evento: e.tipo_evento,
        data: e.created_at,
        user_id: e.user_id,
        payload: JSON.stringify(e.payload ?? ''),
      }));

      const headers = Object.keys(rows[0]);
      const sanitize = (v: string) => {
        let s = String(v).replace(/[\r\n]+/g, ' ').replace(/"/g, '""');
        if (s.includes(';') || s.includes('"') || s.includes(',')) s = `"${s}"`;
        return s;
      };
      const csv = [headers.join(';'), ...rows.map(r => headers.map(h => sanitize((r as any)[h])).join(';'))].join('\n');
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `adocao_ux_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Exportação concluída' });
    } catch {
      toast({ title: 'Erro ao exportar', variant: 'destructive' });
    }
  }, [toast]);

  const { data, isLoading } = useQuery({
    queryKey: ['ux-adoption-metrics'],
    queryFn: async () => {
      const fourteenDaysAgo = new Date(Date.now() - 14 * 86400000).toISOString();

      const { data: events, error } = await supabase
        .from('emp_eventos_negocio')
        .select('tipo_evento, created_at, user_id, payload')
        .in('tipo_evento', [
          'quick_capture_aberto',
          'quick_capture_submit',
          'execucao_registrada_quick_capture',
          'cockpit_aba_proximo_acessada',
          'cockpit_aba_saldos_acessada',
        ])
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const evts = events || [];
      const abertos = evts.filter(e => e.tipo_evento === 'quick_capture_aberto').length;
      const submits = evts.filter(e => e.tipo_evento === 'quick_capture_submit').length;
      const execRegistradas = evts.filter(e => e.tipo_evento === 'execucao_registrada_quick_capture');
      const proximoAcessos = evts.filter(e => e.tipo_evento === 'cockpit_aba_proximo_acessada').length;
      const saldosAcessos = evts.filter(e => e.tipo_evento === 'cockpit_aba_saldos_acessada').length;

      const uniqueUsers = new Set(
        evts.filter(e => e.tipo_evento === 'quick_capture_submit').map(e => e.user_id)
      ).size;

      const semEvidencia = execRegistradas.filter(e => {
        const p = e.payload as any;
        return p && p.com_evidencia === false;
      }).length;
      const pctSemEvidencia = execRegistradas.length > 0
        ? Math.round((semEvidencia / execRegistradas.length) * 100)
        : 0;

      const conversionRate = abertos > 0 ? Math.round((submits / abertos) * 100) : 0;

      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000);
      const dailySubmits = evts
        .filter(e => e.tipo_evento === 'quick_capture_submit' && new Date(e.created_at) >= sevenDaysAgo)
        .reduce((acc, e) => {
          const day = e.created_at.slice(0, 10);
          acc[day] = (acc[day] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

      const flags: string[] = [];
      if (submits === 0 && abertos > 0) flags.push('Alto abandono: nenhum submit registrado');
      if (conversionRate < 30 && abertos > 3) flags.push(`Conversão crítica: ${conversionRate}%`);
      if (proximoAcessos === 0) flags.push('Próximo Boletim: sem acessos em 14 dias');
      if (saldosAcessos === 0) flags.push('Saldos: sem acessos em 14 dias');
      if (pctSemEvidencia > 70) flags.push(`${pctSemEvidencia}% dos registros sem evidência`);
      if (uniqueUsers <= 1 && submits > 5) flags.push('Uso concentrado em único usuário');

      return {
        abertos, submits, conversionRate, uniqueUsers,
        pctSemEvidencia, proximoAcessos, saldosAcessos,
        dailySubmits, flags, totalEvents: evts.length,
      };
    },
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  if (!data || data.totalEvents === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BarChart3 className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground font-medium">Sem dados de telemetria</p>
          <p className="text-xs text-muted-foreground mt-1">
            A coleta de métricas UX está desativada por padrão. Ative a flag{' '}
            <code className="bg-muted px-1 rounded">ux_analytics_enabled</code> em Configurações para iniciar a coleta quando houver uso real.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Métricas de Adoção UX — Últimos 14 dias
        </h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1.5 text-xs">
            <Download className="h-3.5 w-3.5" />
            Exportar CSV
          </Button>
          <Badge variant="outline" className="text-xs">
            {data.totalEvents} eventos
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <MetricCard
          title="Quick Capture Submits"
          value={data.submits}
          subtitle={`de ${data.abertos} aberturas`}
          icon={<FileCheck className="h-5 w-5" />}
          status={data.submits > 0 ? 'healthy' : 'critical'}
        />
        <MetricCard
          title="Taxa de Conversão"
          value={`${data.conversionRate}%`}
          subtitle={data.conversionRate < 30 ? 'Problema grave de UX' : data.conversionRate < 60 ? 'Aceitável com ajustes' : 'UX validada'}
          icon={<TrendingUp className="h-5 w-5" />}
          status={getConversionStatus(data.conversionRate)}
        />
        <MetricCard
          title="Usuários Únicos"
          value={data.uniqueUsers}
          subtitle="com submit"
          icon={<Users className="h-5 w-5" />}
          status={data.uniqueUsers >= 2 ? 'healthy' : data.uniqueUsers === 1 ? 'warning' : 'critical'}
        />
        <MetricCard
          title="Sem Evidência"
          value={`${data.pctSemEvidencia}%`}
          subtitle={data.pctSemEvidencia > 70 ? 'Risco operacional' : data.pctSemEvidencia > 30 ? 'Aceitável' : 'Comportamento saudável'}
          icon={<AlertTriangle className="h-5 w-5" />}
          status={getEvidenceStatus(data.pctSemEvidencia)}
        />
        <MetricCard
          title="Próximo Boletim"
          value={data.proximoAcessos}
          subtitle={data.proximoAcessos === 0 ? 'Valor não percebido' : 'acessos'}
          icon={<Eye className="h-5 w-5" />}
          status={getUsageStatus(data.proximoAcessos)}
        />
        <MetricCard
          title="Saldos"
          value={data.saldosAcessos}
          subtitle={data.saldosAcessos === 0 ? 'Valor não percebido' : 'acessos'}
          icon={<Eye className="h-5 w-5" />}
          status={getUsageStatus(data.saldosAcessos)}
        />
      </div>

      {Object.keys(data.dailySubmits).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Volume Diário de Submits (7 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-1 h-16">
              {(() => {
                const days: string[] = [];
                for (let i = 6; i >= 0; i--) {
                  const d = new Date(Date.now() - i * 86400000);
                  days.push(d.toISOString().slice(0, 10));
                }
                const maxVal = Math.max(1, ...days.map(d => data.dailySubmits[d] || 0));
                return days.map(day => {
                  const val = data.dailySubmits[day] || 0;
                  const height = Math.max(4, (val / maxVal) * 100);
                  return (
                    <div key={day} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs font-mono text-muted-foreground">{val}</span>
                      <div
                        className="w-full rounded-t bg-primary/60"
                        style={{ height: `${height}%` }}
                      />
                      <span className="text-xs text-muted-foreground">{day.slice(5)}</span>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {data.flags.length > 0 && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Sinais de Problema Detectados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5">
              {data.flags.map((flag, i) => (
                <li key={i} className="text-sm flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-destructive flex-shrink-0" />
                  {flag}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Guia de Leitura dos Dados</CardTitle>
        </CardHeader>
        <CardContent className="text-xs space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <p className="font-medium mb-1">Conversão (aberto → submit)</p>
              <div className="space-y-0.5">
                <p><Badge variant="destructive" className="text-xs h-4">{'< 30%'}</Badge> Problema grave de UX</p>
                <p><Badge variant="secondary" className="text-xs h-4">30–60%</Badge> Aceitável com ajustes</p>
                <p><Badge variant="default" className="text-xs h-4">{'>60%'}</Badge> UX validada</p>
              </div>
            </div>
            <div>
              <p className="font-medium mb-1">% Sem Evidência</p>
              <div className="space-y-0.5">
                <p><Badge variant="destructive" className="text-xs h-4">{'>70%'}</Badge> Risco operacional</p>
                <p><Badge variant="secondary" className="text-xs h-4">30–70%</Badge> Aceitável</p>
                <p><Badge variant="default" className="text-xs h-4">{'<30%'}</Badge> Saudável</p>
              </div>
            </div>
            <div>
              <p className="font-medium mb-1">Acesso a abas de Gestão</p>
              <div className="space-y-0.5">
                <p><Badge variant="destructive" className="text-xs h-4">0 em 7d</Badge> Sem valor percebido</p>
                <p><Badge variant="secondary" className="text-xs h-4">1–3</Badge> Exploratório</p>
                <p><Badge variant="default" className="text-xs h-4">≥4</Badge> Feature validada</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UxAdoptionMetricsPanel;
